import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import DownloadInstagramContent from './DownloadInstagramContent';
import ChooseContent from './ChooseContent';
import WhyDownloadStoriesContent from './WhyDownloadStoriesContent';
import FAQContent from './FAQContent';
import BestTipsContent from '../LandingPage/BestTipsContent';
import DownloadStoriesBottomContent from '../PostComments/DownloadStoriesBottomContent';
const AlterModal = dynamic(() => import('./AlterModal'), { ssr: false });
const AlterModalLimit = dynamic(() => import('./AlterModalLimit'), { ssr: false });
import CommentsPlan from './CommentsPlan';
import {
  fetchGetExportNumber,
  fetchOrderSubsListRequest,
  fetchCreateTaskApi,
  fetchGetTaskListApi,
} from '@/lib/service/home';
import useRegisterOrLoginGuide from '@/lib/hooks/common/useRegisterOrLoginGuide';
import { message } from 'antd';
import { fetchInsMonitorSearch } from '@/lib/service';
import { InsAccountModel } from '@/types/home';
import { isInsUserUrl } from '@/lib/utils';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import useFollowExportPremium from '@/lib/hooks/isPremium/useFollowExportPremium';
import { useRouter } from 'next/router';

export interface ITaskListData {
  task: Task;
  pages: Pages;
}

export interface Task {
  id: number;
  uid: string;
  target_media_name: string;
  export_type: string;
  is_paid_export: boolean;
  export_count: number;
  exported_users: any;
  remark: any;
  media_name: string;
  full_name: string;
  profile_pic_url: string;
  private_mark: any;
  verified: boolean;
  media_url: string;
  current_export_count: number;
  status: number;
  insert_time: string;
  follower_count: number;
  following_count: number;
  media_count: number;
  csv_url: string;
  excel_url: string;
}

export interface Pages {
  message: string;
  code: number;
  total: number;
  data: Daum[];
  has_next: boolean;
}

export interface Daum {
  id: string;
  task_id: number;
  pk_id: string;
  profile_pic_url: string;
  username: string;
  full_name: string;
  verified: boolean;
}

const Searching = dynamic(() => import('./Searching'), { ssr: false });
const StartTastModal = dynamic(() => import('./StartTastModal'), { ssr: false });

const TopSearchContent = dynamic(() => import('./TopSearch'), { ssr: true });
const CommonSimpleModal = dynamic(() => import('../Common/CommonSimpleModal'), { ssr: false });
const UserDashboard = dynamic(() => import('../Dashboard/UserDashboard'), { ssr: false });
const InstagramUserInfo = dynamic(() => import('./InstagramUserInfo'), { ssr: false });
const DataPanel = dynamic(() => import('./DataPanel'), { ssr: false });

export default function IgFollowerExport({ data }: any) {
  const contentData = data?.fetchDataRes;
  const { t } = useTranslation('common');
  const [searchStr, setSearchStr] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUserPlan, setCurrentUserPlan] = useState<any>(null);
  const [isPlanReady, setIsPlanReady] = useState(false);
  const { isLogin, guideToRegisterOrLogin } = useRegisterOrLoginGuide();
  const [currentInsUser, setCurrentInsUser] = useState<InsAccountModel>(null);
  const currentUserRef = useRef<InsAccountModel>(null);
  const [openPrivateAccount, setOpenPrivateAccount] = useState<any>(null);
  const [openStartTastModal, setOpenStartTastModal] = useState<boolean>(false);
  const { isPremium } = useFollowExportPremium();
  const onValueChanged = (e?: string) => {
    setSearchStr(e);
  };

  const router = useRouter();

  // 是否是可以导出
  const isUse = useMemo(() => {
    return currentUserPlan?.status == 'normal';
  }, [currentUserPlan]);

  const [type, setType] = useState<'following' | 'followers'>('followers');
  const originalSearchStrRef = useRef<string>('');

  useEffect(() => {
    if (!router.isReady || !isPlanReady) return;
    const { username, relation, type, title } = router.query;
    if (username && typeof username === 'string') {
      setSearchStr(decodeURIComponent(username));
    }
    if (type && typeof type === 'string') {
      setType(type as 'following' | 'followers');
    }
    if (relation && (relation === 'followers' || relation === 'following')) {
      setType(relation);
    }
    if (title) {
      const PricingTitle = document.querySelector(`#${title}`);
      if (PricingTitle) {
        PricingTitle.scrollIntoView({ behavior: 'smooth' });
      }
    }
    // 只有这两个值并存的时候才说明是从dashboard跳转过来的
    if (username && type) {
      onSearch(decodeURIComponent(username as string));
    }
  }, [router.isReady, router.query, isPlanReady]);

  const onSearch = async (e?: string) => {
    if (!e) return;
    if (!isLogin) {
      guideToRegisterOrLogin(() => {
        console.log(e);
      });
      return;
    }
    if (isSearchLoading || loading) {
      return;
    }
    setLoading(true);
    const res = await fetchInsMonitorSearch(e, undefined, { bizFuncName: 'follow_export', isPremium });
    setLoading(false);
    if (res.code == 0) {
      const { follower_count, following_count } = res?.data[0];
      if ((type == 'followers' && follower_count == 0) || (type == 'following' && following_count == 0)) {
        message.warning(t('This account has no follower or following data to export.'));
        return;
      }
      setCurrentInsUser(res.data[0]);
      currentUserRef.current = res.data[0];
      const data = {
        is_private: res?.data[0]?.is_private,
        media_name: res?.data[0]?.media_name,
        profile_pic_url: res?.data[0]?.profile_pic_url,
      };
      if (data?.is_private == 1) {
        setOpenPrivateAccount({
          title: t('OopsPrivateProfileDetected'),
          desc: (
            <>
              <div style={{ marginBottom: '24px', textAlign: 'center' }}>{t('ThisInstagramAccountIsPrivate_Desc')}</div>
              <UserDashboard {...data} />
            </>
          ),
          btnTitle: t('TRYAGAIN'),
          btnDesc: (
            <p style={{ color: 'var(--app-text-30-color)', marginTop: '10px' }}>
              {t('Note: Only public accounts can be tracked.')}
            </p>
          ),
        });
      } else if (data?.is_private == 2) {
        setOpenPrivateAccount({
          title: t('Invalid Instagram Username'),
          desc: t('The username you entered is not valid. Please check the username and try again.'),
          btnTitle: t('TRYAGAIN'),
        });
      } else {
        if (currentUserPlan?.status != 'normal') {
          handleOpenErrorAlterModal(res.data[0]);
          return;
        }
        // createTask(5000);
        setOpenStartTastModal(true);
      }
    }
  };

  const [taskListData, setTaskListData] = useState<ITaskListData>(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [showPeal, setShowPeal] = useState(false);
  const [openErrorAlterModal, setOpenErrorAlterModal] = useState<any>(null);
  const [openErrorAlterModalLimit, setOpenErrorAlterModalLimit] = useState<any>(null);
  const handleOpenErrorAlterModal = (data?: InsAccountModel) => {
    setOpenErrorAlterModal({
      title: t('Warning'),
      desc: t(
        `Your free plan limits you to exporting only 500 xxx1 at a time, This account has xxx2 xxx3. Would you like to upgrade your plan to export more data?`
      )
        .replace('xxx1', type == 'followers' ? 'followers' : 'following')
        .replace(
          'xxx2',
          type == 'followers' ? data?.follower_count.toLocaleString() : data?.following_count.toLocaleString()
        )
        .replace('xxx3', type == 'followers' ? 'followers' : 'following'),
      btnTitle: t('Upgrade Plan'),
      type: true,
      cancelBtnTitle: t('No, thanks'),
    });
  };

  const createTask = async (exportCount: number) => {
    const res = await fetchCreateTaskApi({
      target_media_name: originalSearchStrRef.current,
      media_name: currentInsUser?.media_name || currentUserRef.current?.media_name,
      full_name: currentInsUser?.full_name || currentUserRef.current?.full_name,
      profile_pic_url: currentInsUser?.profile_pic_url || currentUserRef.current?.profile_pic_url,
      verified: currentInsUser?.is_verified || currentUserRef.current?.is_verified,
      media_url: currentInsUser?.media_url,
      export_type: type,
      export_count: exportCount,
      follower_count: currentInsUser?.follower_count || currentUserRef.current?.follower_count,
      following_count: currentInsUser?.following_count || currentUserRef.current?.following_count,
      media_count: currentInsUser?.media_count || currentUserRef.current?.media_count,
    });
    if (res.code == 0) {
      implamentFunc(res.data.id);
      setShowPeal(true);
      setIsSearchLoading(true);
    } else {
      setCurrentInsUser(null);
      if (res.code == 23005) {
        if (currentUserPlan && currentUserPlan?.status == 'normal') {
          //中级
          if (currentUserPlan?.name == 'Pro Monthly' || currentUserPlan?.name == 'Pro Yearly') {
            setOpenErrorAlterModalLimit({
              title: t('You’ve maxed out your Pro plan'),
              desc: t('Move up to Business to export data from bigger accounts and unlock deeper follower insights.'),
              btnTitle: t('Upgrade to Business'),
            });
          }
          //最高级
          if (currentUserPlan?.name == 'Business Monthly' || currentUserPlan?.name == 'Business Yearly') {
            setOpenErrorAlterModalLimit({
              title: t('You’ve used all your exports for this month'),
              desc: t('Your export limit will refresh automatically next month. Thanks for growing with us.'),
              btnTitle: t('Got it'),
            });
          }
        } else {
          setOpenErrorAlterModalLimit({
            title: t('You’ve hit your free limit'),
            desc: t('Upgrade to unlock full exports and pull follower or following data from any size account.'),
            btnTitle: t('Upgrade'),
          });
        }
      }
    }
  };

  const implamentFunc = async (taskId: string) => {
    let isWhile = true;
    while (isWhile) {
      const res = await fetchGetTaskListApi(taskId, 1, 10);
      if (res.code == 0) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        setTaskListData({ ...res.data });
        if (res.data.task.status === 0) {
        } else {
          isWhile = false;
        }
      }
    }
  };

  const numberRef = useRef<number>(0);
  const [exportNumber, setExportNumber] = useState<number>(0);
  const refreshNumber = async () => {
    const res = await fetchGetExportNumber();
    if (res.code == 0) {
      numberRef.current = res.data;
      setExportNumber(res.data);
    }
  };

  const rightStr = useRef<string>('');

  useEffect(() => {
    if (isLogin) {
      setIsPlanReady(false);
      fetchOrderSubsListRequest('follow_export', data.locale)
        .then((res: any) => {
          setCurrentUserPlan(res.data?.length > 0 ? res.data[res.data.length - 1] : null);
        })
        .catch(() => {
          setCurrentUserPlan(null);
        })
        .finally(() => {
          setIsPlanReady(true);
        });
      refreshNumber();
    } else {
      setCurrentUserPlan(null);
      setIsPlanReady(true);
    }
  }, [isLogin]);

  return (
    <div>
      <div className="max-w-[1200px] mx-auto px-2 md:px-0 mb-10">
        <div>
          <TopSearchContent
            topInfo={contentData?.contentInfo?.hero}
            loading={loading}
            searchVal={searchStr}
            currentInsUser={currentInsUser}
            onSearchClick={(e) => {
              originalSearchStrRef.current = e;
              if (!e) return;
              const searchStr = e.trim();
              if (searchStr.includes('/reel/') || searchStr.includes('/reels/')) {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
              const content = isInsUserUrl(searchStr);
              rightStr.current = content;
              setTaskListData(null);
              if (content) {
                onSearch(content);
              } else {
                setOpenPrivateAccount({
                  title: t('Invalid Instagram Username'),
                  desc: t('The username you entered is not valid. Please check the username and try again.'),
                  btnTitle: t('TRYAGAIN'),
                });
                return;
              }
            }}
            onValueChanged={onValueChanged}
            inputPlaceholder={t('Enter an Instagram username or profile link')}
            middleContent={
              isLogin ? <SubContent currentUserPlan={currentUserPlan} t={t} exportNumber={exportNumber} /> : <></>
            }
            setType={setType}
            type={type}
          />
        </div>
        {currentInsUser && (
          <>
            <InstagramUserInfo currentInsUser={currentInsUser} />
            {showPeal && (
              <div className="border max-w-[1000px] mt-10 shadow-lg rounded-lg mx-auto bg-white">
                {isSearchLoading ? (
                  <Searching
                    onComplete={() => {
                      setIsSearchLoading(false);
                    }}
                  />
                ) : (
                  <DataPanel
                    type={type}
                    currentInsUser={currentInsUser}
                    taskListData={taskListData}
                    isUse={isUse}
                    currentUserPlan={currentUserPlan}
                    refreshNumber={refreshNumber}
                    handleBack={() => {
                      setShowPeal(false);
                      setCurrentInsUser(null);
                      setTaskListData(null);
                      setSearchStr('');
                      numberRef.current = 0;
                      rightStr.current = '';
                    }}
                  />
                )}
              </div>
            )}
          </>
        )}

        <DownloadInstagramContent data={contentData?.contentInfo?.['section-1']} />
        <ChooseContent data={contentData?.contentInfo?.['section-2']} />
        <WhyDownloadStoriesContent data={contentData?.contentInfo?.['section-4']} />
        <CommentsPlan
          currentUserPlan={currentUserPlan}
          guideToRegisterOrLogin={guideToRegisterOrLogin}
          isLogin={isLogin}
        />
        <FAQContent faqInfo={contentData?.contentInfo?.['section-faq']} faqList={contentData?.faqList} />
        <BestTipsContent
          title={contentData?.contentInfo?.['section-top-blog']?.title}
          blogList={contentData?.topBlogList}
        />
        <BestTipsContent title={contentData?.contentInfo?.['section-blog']?.title} blogList={contentData?.blogList} />
      </div>
      <DownloadStoriesBottomContent
        bottomInfo={contentData?.contentInfo['section-buttom']}
        placeholder={t('Enter an Instagram username or profile link')}
        buttonTitle={t('Search Now')}
        loading={false}
        searchVal={searchStr}
        inputVisible={true}
        onValueChanged={(e) => {
          setSearchStr(e);
        }}
        onSearchClick={(e) => {
          document.scrollingElement?.scrollTo({ top: 0, behavior: 'smooth' });
          if (loading || isSearchLoading) return;
          const searchStr = e.trim();
          const content = isInsUserUrl(searchStr);
          rightStr.current = content;
          setTaskListData(null);
          if (content) {
            onSearch(content);
          } else {
            setOpenPrivateAccount({
              title: t('Invalid Instagram Username'),
              desc: t('The username you entered is not valid. Please check the username and try again.'),
              btnTitle: t('TRYAGAIN'),
            });
            return;
          }
        }}
      />

      {/*输入错误弹窗 */}
      <CommonSimpleModal
        open={openPrivateAccount !== null}
        title={openPrivateAccount?.title}
        desc={openPrivateAccount?.desc}
        btnTitle={openPrivateAccount?.btnTitle}
        btnDesc={openPrivateAccount?.btnDesc}
        closeAction={(isOk: boolean) => {
          setOpenPrivateAccount(null);
          setCurrentInsUser(null);
          if (isOk) {
          }
        }}
      />

      {/* 限制弹窗 */}
      <AlterModal
        visible={openErrorAlterModal !== null}
        title={openErrorAlterModal?.title}
        type={openErrorAlterModal?.type}
        btnTitle={openErrorAlterModal?.btnTitle}
        cancelBtnTitle={openErrorAlterModal?.cancelBtnTitle}
        desc={openErrorAlterModal?.desc}
        closeAction={() => {
          setOpenErrorAlterModal(null);
          setCurrentInsUser(null);
        }}
        onCancel={() => {
          setOpenErrorAlterModal(null);
          createTask(500);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          setOpenErrorAlterModal(null);
          setCurrentInsUser(null);
          const PricingTitle = document.querySelector('#pricing-title');
          if (PricingTitle) {
            PricingTitle.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
      {/* 查询点数不够 */}
      <AlterModalLimit
        visible={openErrorAlterModalLimit !== null}
        title={openErrorAlterModalLimit?.title}
        btnTitle={openErrorAlterModalLimit?.btnTitle}
        desc={openErrorAlterModalLimit?.desc}
        onCancel={() => {
          setOpenErrorAlterModalLimit(null);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          if (currentUserPlan?.name == 'Business Monthly' || currentUserPlan?.name == 'Business Yearly') {
            setOpenErrorAlterModalLimit(null);
          } else {
            setOpenErrorAlterModalLimit(null);
            const PricingTitle = document.querySelector('#pricing-title');
            if (PricingTitle) {
              PricingTitle.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }}
      />

      <StartTastModal
        currentUserPlan={currentUserPlan}
        visible={openStartTastModal}
        type={type}
        onClose={() => {
          setOpenStartTastModal(false);
          setCurrentInsUser(null);
        }}
        onExport={(num) => {
          setOpenStartTastModal(false);
          createTask(num);
        }}
        onUpgrade={() => {
          ga_logEvent(EventName.click_export_upgrade_plan);
          setOpenStartTastModal(false);
          const PricingTitle = document.querySelector('#pricing-title');
          if (PricingTitle) {
            PricingTitle.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      />
    </div>
  );
}

const SubContent = ({
  currentUserPlan,
  t,
  exportNumber,
}: {
  currentUserPlan: any;
  t: (s: string) => string;
  exportNumber: number;
}) => {
  const router = useRouter();
  return (
    <div className="flex gap-4 lg:w-[720px] w-full flex-wrap mx-auto  justify-center text-sm">
      <div className="flex gap-2">
        <div className="flex gap-2">
          <span className="font-bold">{t('Export Quota')}:</span>
          <span className="text-green-500">
            {currentUserPlan?.status == 'normal' ? t('Unlimited') : 3000 - exportNumber < 0 ? 0 : 3000 - exportNumber}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="font-bold">{t('Current Plan')}:</span>
          <span className="text-green-500 font-bold">
            {currentUserPlan?.status == 'normal' && currentUserPlan?.name.includes('Business')
              ? t('Business')
              : currentUserPlan?.status == 'normal' && currentUserPlan?.name.includes('Pro')
                ? t('Pro')
                : t('Free')}
          </span>
          {(!currentUserPlan?.name.includes('Business') || currentUserPlan.status != 'normal') && (
            <>
              <span
                className="text-[#725CFF] hover:underline font-bold cursor-pointer hidden lg:block"
                onClick={() => {
                  const PricingTitle = document.querySelector('#pricing-title');
                  if (PricingTitle) {
                    PricingTitle.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {t('Upgrade Now')}
              </span>
              <span
                className="text-[#725CFF] hover:underline font-bold cursor-pointer lg:hidden"
                onClick={() => {
                  const PricingTitle = document.querySelector('#pricing-title');
                  if (PricingTitle) {
                    PricingTitle.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                {t('Upgrade')}
              </span>
            </>
          )}
        </div>
      </div>
      <div
        className="text-[#725CFF] hover:underline font-bold cursor-pointer"
        onClick={() => {
          router.push('/dashboard?dashboard_type=toolkit&featureType=follower_export');
        }}
      >
        {`${t('Export Dashboard')} >`}
      </div>
    </div>
  );
};
